import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, RadarChart, Radar, PolarGrid, 
  PolarAngleAxis, PolarRadiusAxis, ComposedChart, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { 
  Search, Users, Store, Map, Check, X, 
  ArrowUpRight, ArrowDownRight, AlertCircle
} from 'lucide-react';

// Import mock data
import { storePerformance } from '../../data/analyticsData';

// Helper for formatting currency
const formatCurrency = (value) => {
  return value.toLocaleString('en-SA', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-2">{label}</p>
        <div className="space-y-1.5">
          {payload.map((entry, index) => (
            <p 
              key={index} 
              className="text-sm" 
              style={{ color: entry.color }}
            >
              {entry.name}: {
                entry.name.includes('Sales') ? formatCurrency(entry.value) :
                entry.name.includes('Rate') ? `${entry.value.toFixed(1)}%` :
                entry.value.toLocaleString()
              }
            </p>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

const PerformanceComparison = ({ filters }) => {
  const [selectedStores, setSelectedStores] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeOptions, setStoreOptions] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [comparisonMetrics, setComparisonMetrics] = useState([
    { id: 'sales', name: 'Sales', selected: true },
    { id: 'transactions', name: 'Transactions', selected: true },
    { id: 'avgTicket', name: 'Avg. Ticket', selected: true },
    { id: 'refundRate', name: 'Refund Rate', selected: true },
    { id: 'discountRate', name: 'Discount Rate', selected: true }
  ]);
  
  // Filter store options based on search query and filters
  useEffect(() => {
    let filteredStores = [...storePerformance];
    
    // Filter by region if specified
    if (filters.region !== 'all') {
      filteredStores = filteredStores.filter(store => 
        store.region.toLowerCase().includes(filters.region.toLowerCase())
      );
    }
    
    // Filter by store type if specified
    if (filters.storeType !== 'all') {
      filteredStores = filteredStores.filter(store => 
        store.type.toLowerCase().replace('+', '').includes(filters.storeType.toLowerCase())
      );
    }
    
    // Filter by search query
    if (searchQuery) {
      filteredStores = filteredStores.filter(store => 
        store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        store.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setStoreOptions(filteredStores);
  }, [searchQuery, filters]);
  
  // Update comparison data when selected stores change
  useEffect(() => {
    // For each metric, create a data point for each store
    const metrics = comparisonMetrics.filter(m => m.selected).map(m => m.id);
    
    // Generate data for bar chart comparison
    const barData = metrics.map(metric => {
      const dataPoint = { name: comparisonMetrics.find(m => m.id === metric).name };
      
      selectedStores.forEach(storeId => {
        const store = storePerformance.find(s => s.id === storeId);
        if (store) {
          dataPoint[store.name] = store[metric];
        }
      });
      
      return dataPoint;
    });
    
    setComparisonData(barData);
  }, [selectedStores, comparisonMetrics]);
  
  // Toggle store selection
  const toggleStoreSelection = (storeId) => {
    if (selectedStores.includes(storeId)) {
      setSelectedStores(selectedStores.filter(id => id !== storeId));
    } else {
      // Limit to 5 stores for better visualization
      if (selectedStores.length < 5) {
        setSelectedStores([...selectedStores, storeId]);
      }
    }
  };
  
  // Toggle metric selection
  const toggleMetricSelection = (metricId) => {
    setComparisonMetrics(metrics => 
      metrics.map(metric => 
        metric.id === metricId 
          ? { ...metric, selected: !metric.selected }
          : metric
      )
    );
  };
  
  // Generate colors for bars
  const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ec4899'];
  
  // Format radar data
  const radarData = selectedStores.map(storeId => {
    const store = storePerformance.find(s => s.id === storeId);
    if (!store) return null;
    
    // Normalize values for radar chart (0-100 scale)
    return {
      name: store.name,
      sales: Math.min(100, (store.sales / 1000000) * 100), // Scale to fit radar
      transactions: Math.min(100, (store.transactions / 10000) * 100),
      avgTicket: Math.min(100, (store.avgTicket / 200) * 100),
      refundRate: Math.min(100, store.refundRate * 20), // Amplify for visibility
      discountRate: Math.min(100, store.discountRate * 10) // Amplify for visibility
    };
  }).filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Store Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Store Comparison
        </h3>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Store Selection Panel */}
          <div className="w-full md:w-1/3">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search stores..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
            
            <div className="h-[300px] overflow-y-auto pr-2 space-y-2">
              {storeOptions.slice(0, 20).map(store => (
                <button
                  key={store.id}
                  onClick={() => toggleStoreSelection(store.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg text-sm ${
                    selectedStores.includes(store.id)
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-3">
                      <Store className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{store.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{store.region}</p>
                    </div>
                  </div>
                  <div>
                    {selectedStores.includes(store.id) ? (
                      <Check className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                    ) : (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{store.type}</span>
                    )}
                  </div>
                </button>
              ))}
              
              {storeOptions.length === 0 && (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  No stores found matching your criteria
                </div>
              )}
            </div>
            
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {selectedStores.length} of 5 stores selected
            </div>
          </div>
          
          {/* Comparison Visualization */}
          <div className="w-full md:w-2/3">
            {selectedStores.length > 0 ? (
              <div className="space-y-4">
                {/* Metric Selection */}
                <div className="flex flex-wrap gap-2">
                  {comparisonMetrics.map(metric => (
                    <button
                      key={metric.id}
                      onClick={() => toggleMetricSelection(metric.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                        metric.selected
                          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30'
                          : 'bg-gray-50 dark:bg-gray-700/30 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {metric.name}
                    </button>
                  ))}
                </div>
                
                {/* Comparison Charts */}
                <div className="h-[300px]">
                  {selectedStores.length === 1 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center text-gray-500 dark:text-gray-400">
                        <AlertCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                        <p>Select at least one more store to compare</p>
                      </div>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={comparisonData}
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          tick={{ fill: '#6B7280', fontSize: 12 }} 
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        {selectedStores.map((storeId, index) => {
                          const store = storePerformance.find(s => s.id === storeId);
                          if (!store) return null;
                          
                          return (
                            <Bar
                              key={storeId}
                              dataKey={store.name}
                              fill={COLORS[index % COLORS.length]}
                              radius={[0, 4, 4, 0]}
                            />
                          );
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
                
                {/* Radar Comparison - Shown only when at least 2 stores are selected */}
                {selectedStores.length > 1 && (
                  <div className="h-[300px] mt-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Performance Radar Comparison
                    </h4>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart outerRadius={90} data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis 
                          dataKey="name" 
                          tick={{ fill: '#6B7280', fontSize: 12 }}
                        />
                        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        {selectedStores.map((storeId, index) => {
                          const store = storePerformance.find(s => s.id === storeId);
                          if (!store) return null;
                          
                          return (
                            <Radar
                              key={storeId}
                              name={store.name}
                              dataKey={store.name}
                              stroke={COLORS[index % COLORS.length]}
                              fill={COLORS[index % COLORS.length]}
                              fillOpacity={0.2}
                            />
                          );
                        })}
                        <Tooltip />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <Store className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p>Select stores to compare performance</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Regional Comparison Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Regional Performance
        </h3>
        
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={[...new Set(storePerformance.map(store => store.region))].map(region => {
                const regionStores = storePerformance.filter(store => store.region === region);
                const totalSales = regionStores.reduce((sum, store) => sum + store.sales, 0);
                const totalTransactions = regionStores.reduce((sum, store) => sum + store.transactions, 0);
                const avgTicket = totalSales / totalTransactions;
                const storeCount = regionStores.length;
                
                return {
                  name: region,
                  sales: totalSales,
                  transactions: totalTransactions,
                  avgTicket: avgTicket,
                  storeCount: storeCount
                };
              })}
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fill: '#6B7280', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fill: '#6B7280', fontSize: 12 }}
                tickFormatter={(value) => formatCurrency(value).replace('SAR ', '')}
              />
              <YAxis 
                yAxisId="right" 
                orientation="right" 
                tick={{ fill: '#6B7280', fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                yAxisId="left"
                dataKey="sales" 
                name="Total Sales" 
                fill="#3b82f6" 
                fillOpacity={0.8} 
                barSize={40}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="transactions" 
                name="Transactions" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="avgTicket" 
                name="Avg. Ticket" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ r: 5 }}
                activeDot={{ r: 7 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Insights */}
      <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-800/20">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800/30">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-blue-800 dark:text-blue-300">Comparison Insights</h3>
            <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Riyadh Region has the highest total sales but Makkah Region shows better average ticket value.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Stores with Drive-thru capabilities consistently outperform traditional café formats.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Eastern Province shows the lowest refund rates, indicating high customer satisfaction.</span>
              </li>
              <li className="flex items-start gap-1">
                <span>•</span>
                <span>Consider expanding the Café+DT format in regions with high growth potential.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceComparison; 